import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { BookOpen, ArrowLeft, ShieldAlert, KeyRound, RefreshCw, UserX, CheckCircle2, Loader2, Users, Search, CircleDollarSign, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const API = process.env.REACT_APP_BACKEND_URL;

  const [audit, setAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);

  // User list state
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({ total_users: 0, paid_users: 0, unpaid_users: 0 });
  const [totalMatching, setTotalMatching] = useState(0);
  const [userFilter, setUserFilter] = useState('all'); // all | paid | unpaid
  const [userSearch, setUserSearch] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [deletingEmail, setDeletingEmail] = useState('');

  // Non-admin guard: redirect to dashboard if a non-admin lands here
  useEffect(() => {
    if (!loading && user && user.role !== 'admin') {
      navigate('/dashboard');
    }
    if (!loading && !user) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  // Fetch users when filter/search changes
  useEffect(() => {
    if (user?.role !== 'admin') return;
    const timeout = setTimeout(() => fetchUsers(), 350); // debounce search input
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFilter, userSearch, user]);

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('filter', userFilter);
      if (userSearch.trim()) params.set('q', userSearch.trim());
      params.set('limit', '100');
      const { data } = await axios.get(
        `${API}/api/admin/users?${params.toString()}`,
        { withCredentials: true }
      );
      setUsers(data.users);
      setStats(data.stats);
      setTotalMatching(data.total);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  const deleteUser = async (email) => {
    if (!window.confirm(`⚠ PERMANENTLY DELETE ${email}?\n\nThis will also delete:\n• Their books, book clubs, meetings\n• All their messages and connections\n• Their payment history\n\nThis CANNOT be undone. Continue?`)) return;
    setDeletingEmail(email);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/delete-user`,
        { email },
        { withCredentials: true }
      );
      toast.success(data.message || `Deleted ${email}`);
      await fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingEmail('');
    }
  };

  const runAudit = async () => {
    setAuditLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/paywall-audit`, { withCredentials: true });
      setAudit(data);
      toast.success(`Audit complete — ${data.count} suspect user${data.count === 1 ? '' : 's'} found`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to run audit');
    } finally {
      setAuditLoading(false);
    }
  };

  const resetPaywall = async (email) => {
    if (!email) return;
    if (!window.confirm(`Reset paywall for ${email}? They will need to pay $10 again to access paid features.`)) return;
    setResetLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/admin/reset-paywall`,
        { email },
        { withCredentials: true }
      );
      toast.success(data.message || `Reset ${email}`);
      setResetEmail('');
      // Re-run audit to refresh the list
      await runAudit();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally {
      setResetLoading(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPwd !== confirmPwd) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPwd.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    setPwdLoading(true);
    try {
      await axios.post(
        `${API}/api/admin/change-password`,
        { current_password: currentPwd, new_password: newPwd },
        { withCredentials: true }
      );
      toast.success('Admin password updated. Keep it safe! 🔐');
      setCurrentPwd(''); setNewPwd(''); setConfirmPwd('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Password update failed');
    } finally {
      setPwdLoading(false);
    }
  };

  if (loading || !user || user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <Loader2 className="w-8 h-8 text-[#6B7A4F] animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-panel" className="min-h-screen bg-[#FDFBF7] paper-texture">
      <nav className="bg-white/80 backdrop-blur-xl border-b border-[#E1D9CD] sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-[#6B7A4F]" />
              <span className="text-xl font-bold text-[#2D2A26]">Bookish Bestie</span>
              <span className="hidden sm:inline text-[10px] tracking-[0.2em] uppercase bg-[#6B7A4F] text-white px-2 py-1 rounded-full ml-2">
                Admin
              </span>
            </div>
            <Link to="/dashboard">
              <Button
                variant="outline"
                className="border-[#6B7A4F] text-[#6B7A4F] hover:bg-[#6B7A4F] hover:text-white"
                data-testid="admin-back-to-dashboard"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 py-8 max-w-3xl">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-[#6B7A4F] to-[#434E30] mb-3 shadow-lg">
            <ShieldAlert className="w-7 h-7 text-white" strokeWidth={2} />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#2D2A26]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Admin Panel
          </h1>
          <p className="text-sm text-[#5C5955] mt-2">Manage users, paywall status, and account security.</p>
        </div>

        {/* --------- User List (stats + search + filter) --------- */}
        <section className="glass-card p-6 rounded-2xl mb-6" data-testid="user-list-section">
          <div className="flex items-center gap-3 mb-4">
            <Users className="w-5 h-5 text-[#6B7A4F]" />
            <h2 className="text-xl font-semibold text-[#2D2A26]">Everyone Who Signed Up</h2>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
            <div className="rounded-xl p-3 sm:p-4 text-center bg-white/70 border border-[#E1D9CD]" data-testid="stat-total">
              <p className="text-2xl sm:text-3xl font-bold text-[#2D2A26]">{stats.total_users}</p>
              <p className="text-[10px] sm:text-xs uppercase tracking-wider text-[#5C5955] font-semibold mt-1">Signups</p>
            </div>
            <div className="rounded-xl p-3 sm:p-4 text-center bg-[#E2E6D5]/50 border border-[#6B7A4F]/30" data-testid="stat-paid">
              <p className="text-2xl sm:text-3xl font-bold text-[#6B7A4F]">{stats.paid_users}</p>
              <p className="text-[10px] sm:text-xs uppercase tracking-wider text-[#6B7A4F] font-semibold mt-1 flex items-center justify-center gap-1">
                <CircleDollarSign className="w-3 h-3" /> Paid
              </p>
            </div>
            <div className="rounded-xl p-3 sm:p-4 text-center bg-white/70 border border-[#E1D9CD]" data-testid="stat-unpaid">
              <p className="text-2xl sm:text-3xl font-bold text-[#5C5955]">{stats.unpaid_users}</p>
              <p className="text-[10px] sm:text-xs uppercase tracking-wider text-[#5C5955] font-semibold mt-1">Not Paid</p>
            </div>
          </div>

          {/* Filter tabs */}
          <div className="flex gap-2 mb-3">
            {['all', 'paid', 'unpaid'].map((f) => (
              <button
                key={f}
                onClick={() => setUserFilter(f)}
                data-testid={`user-filter-${f}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  userFilter === f
                    ? 'bg-[#6B7A4F] text-white'
                    : 'bg-white/70 text-[#5C5955] border border-[#E1D9CD] hover:bg-[#E2E6D5]/40'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5955]" />
            <Input
              type="text"
              placeholder="Search by name or email..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              data-testid="user-search-input"
              className="pl-9 bg-white/70 border-[#E1D9CD]"
            />
          </div>

          {/* Results */}
          {usersLoading ? (
            <div className="flex items-center justify-center py-8 text-[#5C5955]">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading users...
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-8 text-[#5C5955] text-sm">
              No users match your filter.
            </div>
          ) : (
            <>
              <p className="text-xs text-[#5C5955] mb-3">
                Showing {users.length} of {totalMatching}
              </p>
              <div className="space-y-2" data-testid="user-list">
                {users.map((u) => (
                  <div
                    key={u._id}
                    data-testid={`user-row-${u.email}`}
                    className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 sm:p-4 bg-white/70 border border-[#E1D9CD] rounded-xl"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-[#2D2A26] truncate">
                          {u.name || '(no name)'}
                        </p>
                        {u.has_paid && (
                          <span className="text-[10px] font-bold uppercase tracking-wider bg-[#6B7A4F] text-white px-2 py-0.5 rounded-full flex items-center gap-1">
                            <CircleDollarSign className="w-3 h-3" /> Paid
                          </span>
                        )}
                        {u.role === 'admin' && (
                          <span className="text-[10px] font-bold uppercase tracking-wider bg-[#2D2A26] text-white px-2 py-0.5 rounded-full">
                            Admin
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#5C5955] truncate">{u.email}</p>
                      <p className="text-[11px] text-[#5C5955] mt-1">
                        {u.city && <span>📍 {u.city} · </span>}
                        joined {(u.created_at || '').slice(0, 10)}
                        {u.reader_vibe && <span> · vibe: {u.reader_vibe}</span>}
                      </p>
                    </div>
                    {u.has_paid && u.role !== 'admin' && (
                      <Button
                        onClick={() => resetPaywall(u.email)}
                        disabled={resetLoading}
                        data-testid={`user-row-reset-${u.email}`}
                        size="sm"
                        variant="outline"
                        className="border-[#6B7A4F] text-[#6B7A4F] hover:bg-[#6B7A4F] hover:text-white rounded-full whitespace-nowrap text-xs"
                      >
                        Reset
                      </Button>
                    )}
                    {u.role !== 'admin' && (
                      <Button
                        onClick={() => deleteUser(u.email)}
                        disabled={deletingEmail === u.email}
                        data-testid={`user-row-delete-${u.email}`}
                        size="sm"
                        variant="outline"
                        className="border-red-500 text-red-600 hover:bg-red-500 hover:text-white rounded-full whitespace-nowrap text-xs"
                      >
                        {deletingEmail === u.email ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </section>

        {/* --------- Paywall Audit --------- */}
        <section className="glass-card p-6 rounded-2xl mb-6" data-testid="paywall-audit-section">
          <div className="flex items-center gap-3 mb-4">
            <UserX className="w-5 h-5 text-[#6B7A4F]" />
            <h2 className="text-xl font-semibold text-[#2D2A26]">Paywall Audit</h2>
          </div>
          <p className="text-sm text-[#5C5955] mb-4">
            Find users who were wrongly granted access without a completed payment.
          </p>
          <Button
            onClick={runAudit}
            disabled={auditLoading}
            data-testid="run-audit-btn"
            className="bg-[#6B7A4F] hover:bg-[#57653F] text-white rounded-full mb-4"
          >
            {auditLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            {auditLoading ? 'Auditing…' : 'Run Audit'}
          </Button>

          {audit && (
            <div className="mt-4" data-testid="audit-results">
              {audit.count === 0 ? (
                <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-green-800 font-medium">
                    All clear! No suspect users found.
                  </span>
                </div>
              ) : (
                <>
                  <p className="text-sm text-[#6B7A4F] font-semibold mb-3">
                    {audit.count} suspect user{audit.count === 1 ? '' : 's'}:
                  </p>
                  <div className="space-y-2">
                    {audit.suspect_users.map((u) => (
                      <div
                        key={u.user_id}
                        data-testid={`suspect-user-${u.email}`}
                        className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-white/70 border border-[#E1D9CD] rounded-xl"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-[#2D2A26] truncate">{u.email}</p>
                          <p className="text-xs text-[#5C5955]">{u.name || '(no name)'} · granted {u.paid_at?.split('T')[0] || 'unknown'}</p>
                        </div>
                        <Button
                          onClick={() => resetPaywall(u.email)}
                          disabled={resetLoading}
                          data-testid={`reset-btn-${u.email}`}
                          size="sm"
                          className="bg-[#6B7A4F] hover:bg-[#57653F] text-white rounded-full whitespace-nowrap"
                        >
                          Reset
                        </Button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </section>

        {/* --------- Manual Reset --------- */}
        <section className="glass-card p-6 rounded-2xl mb-6" data-testid="manual-reset-section">
          <div className="flex items-center gap-3 mb-4">
            <RefreshCw className="w-5 h-5 text-[#6B7A4F]" />
            <h2 className="text-xl font-semibold text-[#2D2A26]">Reset Any User's Paywall</h2>
          </div>
          <p className="text-sm text-[#5C5955] mb-4">
            Manually clear a user's <code className="text-xs bg-[#E2E6D5]/40 px-1 rounded">has_paid</code> flag. They'll need to pay $10 again.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              type="email"
              placeholder="user@example.com"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              data-testid="manual-reset-email-input"
              className="flex-1 bg-white/70 border-[#E1D9CD]"
            />
            <Button
              onClick={() => resetPaywall(resetEmail)}
              disabled={!resetEmail || resetLoading}
              data-testid="manual-reset-btn"
              className="bg-[#6B7A4F] hover:bg-[#57653F] text-white rounded-full"
            >
              {resetLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Reset User
            </Button>
          </div>
        </section>

        {/* --------- Change Admin Password --------- */}
        <section className="glass-card p-6 rounded-2xl" data-testid="change-password-section">
          <div className="flex items-center gap-3 mb-4">
            <KeyRound className="w-5 h-5 text-[#6B7A4F]" />
            <h2 className="text-xl font-semibold text-[#2D2A26]">Change Admin Password</h2>
          </div>
          <p className="text-sm text-[#5C5955] mb-4">
            🔐 <strong>Do this now.</strong> The default password <code className="text-xs bg-[#E2E6D5]/40 px-1 rounded">admin123</code> is publicly documented and anyone could log in.
          </p>
          <form onSubmit={changePassword} className="space-y-3">
            <div>
              <Label htmlFor="current-pwd" className="text-[#5C5955]">Current password</Label>
              <Input
                id="current-pwd"
                type="password"
                value={currentPwd}
                onChange={(e) => setCurrentPwd(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="current-pwd-input"
                className="bg-white/70 border-[#E1D9CD]"
              />
            </div>
            <div>
              <Label htmlFor="new-pwd" className="text-[#5C5955]">New password (min 8 characters)</Label>
              <Input
                id="new-pwd"
                type="password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                data-testid="new-pwd-input"
                className="bg-white/70 border-[#E1D9CD]"
              />
            </div>
            <div>
              <Label htmlFor="confirm-pwd" className="text-[#5C5955]">Confirm new password</Label>
              <Input
                id="confirm-pwd"
                type="password"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                required
                autoComplete="new-password"
                data-testid="confirm-pwd-input"
                className="bg-white/70 border-[#E1D9CD]"
              />
            </div>
            <Button
              type="submit"
              disabled={pwdLoading}
              data-testid="change-password-btn"
              className="w-full bg-[#6B7A4F] hover:bg-[#57653F] text-white rounded-full"
            >
              {pwdLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {pwdLoading ? 'Updating…' : 'Update Password'}
            </Button>
          </form>
        </section>
      </div>
    </div>
  );
};

export default AdminPanel;
